import {
  defaultLanguageForCountry,
  detectVisitorCountryCodeSync,
} from '@/lib/visitor-geo-locale';

/** Langues supportées par l’app (aligné sur i18n `supportedLngs`). */
export type Language = 'fr' | 'en' | 'lb' | 'it' | 'es' | 'uk' | 'ru' | 'pt' | 'de' | 'ar' | 'tr' | 'zgh';

const SUPPORTED_LANGUAGE_CODES = ['fr', 'en', 'lb', 'it', 'es', 'uk', 'ru', 'pt', 'de', 'ar', 'tr', 'zgh'] as const;

function isSupportedLanguage(code: string): code is Language {
  return (SUPPORTED_LANGUAGE_CODES as readonly string[]).includes(code);
}

const FALLBACK_LANGUAGE: Language = 'en';

export function hasUserPersistedLanguage(): boolean {
  return readUserPersistedLanguage() !== null;
}

export function readUserPersistedLanguage(): Language | null {
  if (typeof window === 'undefined') return null;
  try {
    const app = localStorage.getItem('app_language');
    if (app && isSupportedLanguage(app)) return app;
    const rawLocale = localStorage.getItem('locale');
    const locCode = rawLocale?.split('-')[0] ?? '';
    if (locCode && isSupportedLanguage(locCode)) return locCode;
  } catch (error) {
    console.warn('Error reading language from localStorage:', error);
  }
  return null;
}

/**
 * Langue initiale : préférence utilisateur persistée, sinon défaut géographique
 * (MA → amazighe, FR/CH/LU/BE → français, reste → anglais).
 */
export function resolveInitialLanguage(): Language {
  if (typeof window === 'undefined') return FALLBACK_LANGUAGE;

  const persisted = readUserPersistedLanguage();
  if (persisted) return persisted;

  return defaultLanguageForCountry(detectVisitorCountryCodeSync()) as Language;
}

export function persistUserLanguage(language: Language): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem('app_language', language);
    localStorage.setItem('locale', language);
    document.cookie = `app_language=${encodeURIComponent(language)};path=/;max-age=31536000;SameSite=Lax`;
  } catch (error) {
    console.warn('Error saving language to localStorage:', error);
  }
}

/** Synchronise le cookie nginx à partir du localStorage (migration visiteurs existants). */
export function syncMarketingLanguageCookie(): void {
  if (typeof window === 'undefined') return;
  const lang = readUserPersistedLanguage();
  if (!lang) return;
  try {
    document.cookie = `app_language=${encodeURIComponent(lang)};path=/;max-age=31536000;SameSite=Lax`;
  } catch (error) {
    console.warn('Error syncing language cookie:', error);
  }
}
